import { useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { taskCategoriesStore, generateCategoryId } from './taskCategoriesStore';
import type { TaskCategory } from '../types';

export function useTaskCategories(): TaskCategory[] {
  const cachedRef = useRef<TaskCategory[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = taskCategoriesStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newCategories = taskCategoriesStore.getSnapshot().context.categories;
    if (cachedRef.current.length === newCategories.length &&
        cachedRef.current.every((c, i) => c.id === newCategories[i]?.id && c.name === newCategories[i]?.name && c.color === newCategories[i]?.color)) {
      return cachedRef.current;
    }
    cachedRef.current = newCategories;
    return newCategories;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useTaskCategoryById(categoryId: string): TaskCategory | undefined {
  const categories = useTaskCategories();
  return useMemo(() => categories.find(c => c.id === categoryId), [categories, categoryId]);
}

export function useTaskCategoriesActions() {
  return useMemo(() => ({
    addCategory: (category: TaskCategory) =>
      taskCategoriesStore.send({ type: 'category.add', category }),
    addNewCategory: (name: string, color: string) => {
      const id = generateCategoryId(name);
      taskCategoriesStore.send({ type: 'category.add', category: { id, name, color } });
      return id;
    },
    updateCategory: (categoryId: string, updates: Partial<Omit<TaskCategory, 'id'>>) =>
      taskCategoriesStore.send({ type: 'category.update', categoryId, updates }),
    deleteCategory: (categoryId: string) =>
      taskCategoriesStore.send({ type: 'category.delete', categoryId }),
    resetCategories: () =>
      taskCategoriesStore.send({ type: 'categories.reset' }),
    importCategories: (categories: TaskCategory[]) =>
      taskCategoriesStore.send({ type: 'categories.import', categories }),
  }), []);
}
